import { getCanonicalClusterSurname } from './personTreeClusters.mjs';

export const RESEARCH_GROUPS_SCHEMA_VERSION = 1;

const NATURAL_COLLATOR = new Intl.Collator('nl', {
    numeric: true,
    sensitivity: 'base',
});

function compareNaturally(left, right) {
    return NATURAL_COLLATOR.compare(String(left || ''), String(right || ''));
}

function endpointId(endpoint) {
    return endpoint?.personID || endpoint?.id || endpoint;
}

function normalizeBranchIds(branchIds) {
    if (!Array.isArray(branchIds)) return [];
    return [...new Set(branchIds
        .map(branchId => String(branchId || '').trim())
        .filter(Boolean))]
        .sort(compareNaturally);
}

function normalizeOverrides(overrides = {}) {
    return {
        excludedBranchIds: normalizeBranchIds(overrides.excludedBranchIds || ['B2']),
        groupLabelOverrides: overrides.groupLabelOverrides || {},
        leadPersonOverrides: overrides.leadPersonOverrides || {},
    };
}

function validYear(value) {
    if (!value) return Number.POSITIVE_INFINITY;
    const match = String(value).match(/(^|\D)(\d{4})(\D|$)/);
    if (!match || match[2] === '0000') return Number.POSITIVE_INFINITY;
    return Number(match[2]);
}

function personName(person) {
    return `${person?.firstname || ''} ${person?.lastname || ''}`.trim();
}

function comparePeople(left, right) {
    return compareNaturally(left?.lastname, right?.lastname)
        || compareNaturally(left?.firstname, right?.firstname)
        || compareNaturally(left?.personID, right?.personID);
}

function publicMember(person, generation) {
    return {
        personID: person.personID,
        firstname: person.firstname || '',
        lastname: person.lastname || '',
        label: person.label || personName(person) || person.personID,
        sex: person.sex || 'unknown',
        living: person.living || 0,
        birthdatetr: person.birthdatetr || null,
        altbirthdatetr: person.altbirthdatetr || null,
        deathdatetr: person.deathdatetr || null,
        burialdatetr: person.burialdatetr || null,
        photoPath: person.photoPath || null,
        branchIds: normalizeBranchIds(person.branchIds),
        generation: Number.isInteger(generation) ? generation : null,
    };
}

function buildComponents(personIds, links) {
    const adjacency = new Map([...personIds].map(personID => [personID, new Set()]));

    for (const link of links) {
        adjacency.get(link.source)?.add(link.target);
        adjacency.get(link.target)?.add(link.source);
    }

    const visited = new Set();
    const components = [];
    for (const personID of [...personIds].sort(compareNaturally)) {
        if (visited.has(personID)) continue;

        const component = [];
        const queue = [personID];
        visited.add(personID);
        let cursor = 0;
        while (cursor < queue.length) {
            const current = queue[cursor];
            cursor += 1;
            component.push(current);
            for (const neighbor of adjacency.get(current) || []) {
                if (visited.has(neighbor)) continue;
                visited.add(neighbor);
                queue.push(neighbor);
            }
        }
        components.push(component.sort(compareNaturally));
    }

    return components;
}

function componentSurname(componentPeople, branchIds) {
    const counts = new Map();
    const labelsByKey = new Map();
    const isMolukseBranchComponent = branchIds.includes('B4');

    for (const person of componentPeople) {
        const surname = String(person.lastname || '').trim();
        if (!surname) continue;
        const canonical = getCanonicalClusterSurname(surname, { isMolukseBranchComponent });
        if (!canonical.key) continue;
        counts.set(canonical.key, (counts.get(canonical.key) || 0) + 1);
        if (!labelsByKey.has(canonical.key)) {
            labelsByKey.set(canonical.key, canonical.label || surname);
        }
    }

    const [topEntry] = [...counts.entries()].sort((left, right) => {
        if (right[1] !== left[1]) return right[1] - left[1];
        return compareNaturally(left[0], right[0]);
    });

    if (!topEntry) return { key: 'unknown', label: '' };
    return {
        key: topEntry[0],
        label: labelsByKey.get(topEntry[0]) || topEntry[0],
    };
}

function descendantReach(personID, childrenByParent) {
    const visited = new Set();
    const queue = [...(childrenByParent.get(personID) || [])];
    while (queue.length) {
        const childID = queue.pop();
        if (visited.has(childID)) continue;
        visited.add(childID);
        queue.push(...(childrenByParent.get(childID) || []));
    }
    return visited.size;
}

function assignGenerations(componentIds, roots, childrenByParent) {
    const generation = new Map(roots.map(personID => [personID, 0]));
    const queue = [...roots];
    let cursor = 0;

    while (cursor < queue.length) {
        const parentID = queue[cursor];
        cursor += 1;
        const childGeneration = generation.get(parentID) + 1;
        for (const childID of childrenByParent.get(parentID) || []) {
            if (!componentIds.has(childID)) continue;
            const knownGeneration = generation.get(childID);
            if (knownGeneration !== undefined && knownGeneration <= childGeneration) continue;
            generation.set(childID, childGeneration);
            queue.push(childID);
        }
    }

    return generation;
}

function selectLead({ candidates, peopleById, surnameKey, childrenByParent, isMolukseBranchComponent }) {
    return [...candidates].sort((leftID, rightID) => {
        const left = peopleById.get(leftID);
        const right = peopleById.get(rightID);
        const leftSurnameKey = getCanonicalClusterSurname(left?.lastname || '', { isMolukseBranchComponent }).key;
        const rightSurnameKey = getCanonicalClusterSurname(right?.lastname || '', { isMolukseBranchComponent }).key;
        const leftMatches = leftSurnameKey === surnameKey ? 1 : 0;
        const rightMatches = rightSurnameKey === surnameKey ? 1 : 0;
        if (rightMatches !== leftMatches) return rightMatches - leftMatches;

        const reachDifference = descendantReach(rightID, childrenByParent)
            - descendantReach(leftID, childrenByParent);
        if (reachDifference !== 0) return reachDifference;

        const leftBirthYear = Math.min(
            validYear(left?.birthdatetr),
            validYear(left?.altbirthdatetr),
        );
        const rightBirthYear = Math.min(
            validYear(right?.birthdatetr),
            validYear(right?.altbirthdatetr),
        );
        if (leftBirthYear !== rightBirthYear) {
            if (!Number.isFinite(leftBirthYear)) return 1;
            if (!Number.isFinite(rightBirthYear)) return -1;
            return leftBirthYear - rightBirthYear;
        }
        return comparePeople(left, right);
    })[0];
}

function validateOverrides(snapshot, overrides) {
    const groupKeys = new Set(snapshot.groups.map(group => group.key));
    const trees = snapshot.groups.flatMap(group => group.trees);
    const treeById = new Map(trees.map(tree => [tree.id, tree]));

    for (const groupKey of Object.keys(overrides.groupLabelOverrides)) {
        if (!groupKeys.has(groupKey)) {
            throw new Error(`Unknown research group label override: ${groupKey}`);
        }
        if (!String(overrides.groupLabelOverrides[groupKey] || '').trim()) {
            throw new Error(`Research group label override must not be empty: ${groupKey}`);
        }
    }

    for (const [treeID, personID] of Object.entries(overrides.leadPersonOverrides)) {
        const tree = treeById.get(treeID);
        if (!tree) throw new Error(`Unknown research tree lead override: ${treeID}`);
        if (!tree.rootPersonIds.includes(personID)) {
            throw new Error(`Lead override ${personID} is not a root of ${treeID}`);
        }
    }
}

export function validateResearchGroupsSnapshot(snapshot) {
    if (!snapshot || snapshot.schemaVersion !== RESEARCH_GROUPS_SCHEMA_VERSION) {
        throw new Error('Unsupported research groups schema');
    }
    if (!Array.isArray(snapshot.groups)) throw new Error('Research groups must be an array');

    const seenPeople = new Set();
    const seenTrees = new Set();
    let personCount = 0;
    let treeCount = 0;

    for (const group of snapshot.groups) {
        if (!group?.key || !Array.isArray(group.trees)) throw new Error('Invalid research group');
        for (const tree of group.trees) {
            if (!tree?.id || seenTrees.has(tree.id)) throw new Error(`Invalid or duplicate research tree: ${tree?.id}`);
            seenTrees.add(tree.id);
            treeCount += 1;
            const memberIds = new Set((tree.members || []).map(member => member.personID));
            if (!memberIds.has(tree.leadPersonId)) throw new Error(`Lead is not a member of ${tree.id}`);
            if (!(tree.rootPersonIds || []).includes(tree.leadPersonId)) throw new Error(`Lead is not a root of ${tree.id}`);
            if (tree.memberCount !== memberIds.size) throw new Error(`Invalid member count for ${tree.id}`);
            for (const personID of memberIds) {
                if (seenPeople.has(personID)) throw new Error(`Research person occurs in multiple trees: ${personID}`);
                seenPeople.add(personID);
                personCount += 1;
            }
            for (const link of tree.links || []) {
                if (!memberIds.has(link.source) || !memberIds.has(link.target)) {
                    throw new Error(`Research link escapes tree ${tree.id}`);
                }
            }
        }
    }

    if (snapshot.personCount !== personCount) throw new Error('Invalid research person total');
    if (snapshot.treeCount !== treeCount) throw new Error('Invalid research tree total');
    return snapshot;
}

export function buildResearchGroups(graph, overrideInput = {}, options = {}) {
    if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.links)) {
        throw new TypeError('A people graph with nodes and links is required');
    }
    if (graph.meta?.privacyScope && graph.meta.privacyScope !== 'public') {
        throw new Error('Research groups may only be generated from a public people graph');
    }

    const overrides = normalizeOverrides(overrideInput);
    const excludedBranchIds = new Set(overrides.excludedBranchIds);
    const peopleById = new Map();

    for (const node of graph.nodes) {
        if (!node?.personID || peopleById.has(node.personID)) continue;
        const activeBranches = normalizeBranchIds(node.branchIds)
            .filter(branchID => !excludedBranchIds.has(branchID));
        if (!activeBranches.length) continue;
        peopleById.set(node.personID, {
            ...node,
            branchIds: normalizeBranchIds(node.branchIds),
            activeBranchIds: activeBranches,
        });
    }

    const eligibleIds = new Set(peopleById.keys());
    const links = [];
    const seenLinks = new Set();
    for (const rawLink of graph.links) {
        if (!['parent', 'marriage'].includes(rawLink?.relation)) continue;
        const source = endpointId(rawLink.source);
        const target = endpointId(rawLink.target);
        if (!eligibleIds.has(source) || !eligibleIds.has(target) || source === target) continue;
        const normalizedEndpoints = rawLink.relation === 'parent'
            ? [source, target]
            : [source, target].sort(compareNaturally);
        const key = `${rawLink.relation}:${normalizedEndpoints.join(':')}`;
        if (seenLinks.has(key)) continue;
        seenLinks.add(key);
        links.push({ source, target, relation: rawLink.relation });
    }

    const groupsByKey = new Map();
    const components = buildComponents(eligibleIds, links);
    for (const component of components) {
        const componentIds = new Set(component);
        const componentPeople = component.map(personID => peopleById.get(personID));
        const componentLinks = links.filter(link => componentIds.has(link.source) && componentIds.has(link.target));
        const parentIds = new Set();
        const childrenByParent = new Map();
        for (const link of componentLinks) {
            if (link.relation !== 'parent') continue;
            parentIds.add(link.target);
            if (!childrenByParent.has(link.source)) childrenByParent.set(link.source, []);
            childrenByParent.get(link.source).push(link.target);
        }

        const naturalRoots = component.filter(personID => !parentIds.has(personID));
        let roots = [...naturalRoots];
        if (!roots.length) roots = [...component];
        roots.sort(compareNaturally);
        const generationById = assignGenerations(componentIds, naturalRoots, childrenByParent);
        const branchIds = normalizeBranchIds(componentPeople.flatMap(person => person.activeBranchIds));
        const surname = componentSurname(componentPeople, branchIds);
        const anchorPersonID = component[0];
        const treeID = `research-tree-${anchorPersonID}`;
        const computedLeadID = selectLead({
            candidates: roots,
            peopleById,
            surnameKey: surname.key,
            childrenByParent,
            isMolukseBranchComponent: branchIds.includes('B4'),
        });
        const leadPersonID = overrides.leadPersonOverrides[treeID] || computedLeadID;
        const members = componentPeople
            .map(person => publicMember(person, generationById.get(person.personID)))
            .sort((left, right) => {
                const leftGeneration = left.generation ?? Number.POSITIVE_INFINITY;
                const rightGeneration = right.generation ?? Number.POSITIVE_INFINITY;
                return leftGeneration - rightGeneration || comparePeople(left, right);
            });

        const tree = {
            id: treeID,
            branchIds,
            leadPersonId: leadPersonID,
            rootPersonIds: roots,
            memberCount: members.length,
            members,
            links: componentLinks.sort((left, right) => (
                compareNaturally(left.source, right.source)
                || compareNaturally(left.target, right.target)
                || compareNaturally(left.relation, right.relation)
            )),
        };

        if (!groupsByKey.has(surname.key)) {
            groupsByKey.set(surname.key, {
                key: surname.key,
                label: surname.label,
                personCount: 0,
                trees: [],
            });
        }
        const group = groupsByKey.get(surname.key);
        group.personCount += members.length;
        group.trees.push(tree);
    }

    const groups = [...groupsByKey.values()]
        .map(group => ({
            ...group,
            label: overrides.groupLabelOverrides[group.key] || group.label,
            trees: group.trees.sort((left, right) => {
                const leftLead = left.members.find(member => member.personID === left.leadPersonId);
                const rightLead = right.members.find(member => member.personID === right.leadPersonId);
                return comparePeople(leftLead, rightLead) || compareNaturally(left.id, right.id);
            }),
        }))
        .sort((left, right) => compareNaturally(left.label || left.key, right.label || right.key));

    const snapshot = {
        schemaVersion: RESEARCH_GROUPS_SCHEMA_VERSION,
        generatedAt: options.generatedAt || new Date().toISOString(),
        sourceSnapshotVersion: graph.meta?.snapshotVersion ?? null,
        excludedBranchIds: [...overrides.excludedBranchIds],
        personCount: peopleById.size,
        treeCount: components.length,
        groups,
    };

    validateOverrides(snapshot, overrides);
    return validateResearchGroupsSnapshot(snapshot);
}
